import * as React from "react";
import { Platform, StyleSheet } from "react-native";
import { View, Text } from "../Themed";
import StatsBlock from "../StatBlock";
import { StatSectionShimmer } from "../OpenHouseListShimmer";
import { useGetDashboardStats } from "../../hooks/useGetDashboardStats";
import GlobalContext from "../../context/Global";
import moment from "moment";
import InputDropdown from "../InputDropdown";
import { useForm } from "react-hook-form";
import Colors from "../../constants/Colors";
import InputDate from "../InputDate";

export default function StatSection() {
  const { user } = React.useContext(GlobalContext);
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      overview: {
        label: "This Month",
        value: {
          startDate: moment().startOf("month").format("YYYY-MM-DD"),
          endDate: moment().endOf("month").format("YYYY-MM-DD"),
          custom: false,
        },
      },
      startDate: new Date(),
      endDate: new Date(),
    },
  });
  const dates = watch("overview");

  const { data: stats, isLoading: loadingStats } = useGetDashboardStats(
    user?.openHouseUuid,
    dates?.value?.startDate,
    dates?.value?.endDate
  );

  const updateDate = (name: string, selectedDate: Date) => {
    const currentDate = selectedDate || new Date();
    setValue(name, new Date(currentDate));
    setValue("overview", {
      ...dates,
      value: {
        ...dates?.value,
        [name]: moment.utc(currentDate).format("YYYY-MM-DD"),
      },
    });
  };

  React.useEffect(() => {
    if (dates?.value?.custom) {
      setValue("startDate", new Date(dates?.value?.startDate));
      setValue("endDate", new Date(dates?.value?.endDate));
    }
  }, [dates]);

  const sortOptions = [
    {
      label: "All Time",
      value: {
        startDate: "2020-01-01",
        endDate: moment().format("YYYY-MM-DD"),
        custom: false,
      },
    },
    {
      label: "Today",
      value: {
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
        custom: false,
      },
    },
    {
      label: "Last 7 Days",
      value: {
        startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
        custom: false,
      },
    },
    {
      label: "Last 14 Days",
      value: {
        startDate: moment().subtract(14, "days").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
        custom: false,
      },
    },
    {
      label: "This Month",
      value: {
        startDate: moment().startOf("month").format("YYYY-MM-DD"),
        endDate: moment().add(1, "days").format("YYYY-MM-DD"),
        custom: false,
      },
    },
    {
      label: "Last Month",
      value: {
        startDate: moment()
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD"),
        endDate: moment()
          .subtract(1, "month")
          .endOf("month")
          .format("YYYY-MM-DD"),
        custom: false,
      },
    },
    {
      label: "This Year",
      value: {
        startDate: moment().startOf("year").format("YYYY-MM-DD"),
        endDate: moment().endOf("year").format("YYYY-MM-DD"),
        custom: false,
      },
    },
    {
      label: "Last Year",
      value: {
        startDate: moment()
          .subtract(1, "year")
          .startOf("year")
          .format("YYYY-MM-DD"),
        endDate: moment()
          .subtract(1, "year")
          .endOf("year")
          .format("YYYY-MM-DD"),
        custom: false,
      },
    },
    {
      label: "Custom",
      value: {
        ...dates?.value,
        custom: true,
      },
    },
  ];

  return loadingStats ? (
    <StatSectionShimmer />
  ) : (
    <View style={styles.statsContainer}>
      <View
        style={{
          zIndex: 99,
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <View
          style={{
            width: dates?.value?.custom ? "40%" : "100%",
          }}
        >
          <Text size={20} lineHeight={28} lightColor={Colors.nuetral_grey_700}>
            Overview
          </Text>
          <InputDropdown
            control={control}
            name={"overview"}
            errors={errors}
            required={false}
            options={sortOptions}
            testingID="overview_dropdown"
            top={Platform.OS === "web" ? 43 : 58}
          />
        </View>
        {dates?.value?.custom && (
          <View style={styles.customContainer}>
            <View style={styles.dateInputContainer}>
              <InputDate
                control={control}
                name="startDate"
                required={false}
                getValues={getValues}
                setValue={(name, value) => updateDate(name, value)}
                defaultValue={new Date(dates?.value?.startDate)}
                maxDate={new Date(dates?.value?.endDate)}
                testingID="start_date_input"
                errors={errors}
                autoSet={false}
              />
            </View>
            <View style={styles.dateInputContainer}>
              <InputDate
                control={control}
                name="endDate"
                required={false}
                getValues={getValues}
                setValue={(name, value) => updateDate(name, value)}
                defaultValue={new Date(dates?.value?.endDate)}
                minDate={new Date(dates?.value?.startDate)}
                testingID="end_date_input"
                errors={errors}
                autoSet={false}
              />
            </View>
          </View>
        )}
      </View>
      <View style={{ flexDirection: "row" }}>
        <View style={styles.stats}>
          <StatsBlock
            number={stats?.openHouses !== null ? stats?.openHouses : 0}
            text="Open Houses"
          />
        </View>
        <View style={styles.stats}>
          <StatsBlock
            number={
              stats?.totalManualAttendees !== null
                ? stats?.totalManualAttendees
                : 0
            }
            text="Attendees"
          />
        </View>
        <View style={styles.stats}>
          <StatsBlock
            number={stats?.totalAttendees !== null ? stats?.totalAttendees : 0}
            text="Leads"
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  statsContainer: {
    padding: 16,
    backgroundColor: "transparent",
    zIndex: 9,
  },
  stats: {
    marginVertical: 8,
    width: "33%",
  },
  customContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginLeft: 24,
    flex: 1,
    marginTop: Platform.OS === "web" ? -46 : 20,
  },
  dateInputContainer: {
    backgroundColor: "transparent",
    width: "45%",
  },
});
